import { memo, type ReactNode } from "react";
import { HelpCircle } from "lucide-react";
import { cn } from "./cn";
import { Tooltip } from "./Tooltip";

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  hint?: string;
}

interface SegmentedControlProps<T extends string> {
  label?: string;
  value: T;
  onChange(v: T): void;
  options: SegmentedOption<T>[];
  className?: string;
  /**
   * Etiketin yanında bir yardım simgesi gösterir; üzerine gelince /
   * odaklanınca bu içerik açılır. Seçenek `hint`'leri yalnız ayarın
   * DEĞERİNİ özetler ("2 deneme"); bu alan ayarın NE İŞE YARADIĞINI
   * anlatmak içindir.
   */
  tooltip?: ReactNode;
}

export function SegmentedControl<T extends string>({
  label,
  value,
  onChange,
  options,
  className,
  tooltip,
}: SegmentedControlProps<T>) {
  return (
    <div className={cn("flex flex-col gap-2 text-sm", className)}>
      {label ? (
        <span className="flex items-center gap-1.5 font-medium text-fg-strong">
          {label}
          {tooltip ? (
            <Tooltip label={tooltip} side="top">
              <button
                type="button"
                // Salt-bilgi tetikleyicisi: klavyeyle odaklanabilir olmalı
                // (Radix tooltip'i focus'ta da açar) ama tıklama bir şey
                // yapmadığı için ekran okuyucuya amacı açıkça bildirilir.
                aria-label={`${label} hakkında bilgi`}
                className="focus-ring cursor-help rounded-full text-fg-subtle transition-colors hover:text-fg-strong"
              >
                <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
              </button>
            </Tooltip>
          ) : null}
        </span>
      ) : null}
      <div
        role="radiogroup"
        className="grid gap-1 rounded-xl border border-white/[0.06] bg-surface-2/50 p-1"
        style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
      >
        {options.map((opt) => {
          const active = opt.value === value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.value)}
              className={cn(
                "focus-ring rounded-lg px-3 py-2 text-center text-xs font-medium transition",
                active
                  ? "bg-[radial-gradient(circle_at_30%_25%,hsl(220_90%_60%/0.5),hsl(265_80%_65%/0.25))] text-fg-strong shadow-[inset_0_0_0_1px_hsl(220_90%_60%/0.4)]"
                  : "text-fg-muted hover:text-fg-strong",
              )}
            >
              <div>{opt.label}</div>
              {opt.hint ? (
                <div className="mt-0.5 text-[10px] uppercase tracking-wider text-fg-subtle">
                  {opt.hint}
                </div>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default memo(SegmentedControl) as typeof SegmentedControl;
